import type { CSSProperties } from "react";

/**
 * Column count for a `SortableList` grid — either a single count at every width, or a per-breakpoint
 * object using Tailwind's breakpoint names. Unspecified breakpoints inherit the next smaller one;
 * `base` defaults to 1.
 */
export type SortableListColumns =
  | number
  | { base?: number; sm?: number; md?: number; lg?: number; xl?: number; "2xl"?: number };

/** Column count a `SortableList.Item` occupies — a number of columns, or every column in the row. */
export type SortableListSpan = number | "full";

// Tailwind's scanner only sees class names written literally in source, so the column count can't
// be interpolated into `grid-cols-N`. These classes are fixed and the count travels as a CSS custom
// property set inline. Each breakpoint's var() falls back to the next smaller one, so a breakpoint
// the consumer didn't specify inherits rather than resetting to base.
export const sortableListColumnClassName =
  "grid-cols-[repeat(var(--sortable-cols),minmax(0,1fr))] " +
  "sm:grid-cols-[repeat(var(--sortable-cols-sm,var(--sortable-cols)),minmax(0,1fr))] " +
  "md:grid-cols-[repeat(var(--sortable-cols-md,var(--sortable-cols-sm,var(--sortable-cols))),minmax(0,1fr))] " +
  "lg:grid-cols-[repeat(var(--sortable-cols-lg,var(--sortable-cols-md,var(--sortable-cols-sm,var(--sortable-cols)))),minmax(0,1fr))] " +
  "xl:grid-cols-[repeat(var(--sortable-cols-xl,var(--sortable-cols-lg,var(--sortable-cols-md,var(--sortable-cols-sm,var(--sortable-cols))))),minmax(0,1fr))] " +
  "2xl:grid-cols-[repeat(var(--sortable-cols-2xl,var(--sortable-cols-xl,var(--sortable-cols-lg,var(--sortable-cols-md,var(--sortable-cols-sm,var(--sortable-cols)))))),minmax(0,1fr))]";

export const sortableListColumnStyle = (columns: SortableListColumns = 1): CSSProperties => {
  if (typeof columns === "number") return { "--sortable-cols": columns };

  const { base = 1, sm, md, lg, xl, "2xl": xxl } = columns;
  return {
    "--sortable-cols": base,
    "--sortable-cols-sm": sm,
    "--sortable-cols-md": md,
    "--sortable-cols-lg": lg,
    "--sortable-cols-xl": xl,
    "--sortable-cols-2xl": xxl,
  };
};

export const sortableListSpanClassName = (span: SortableListSpan | undefined): string | undefined => {
  if (span === "full") return "col-span-full";
  if (span === undefined) return undefined;
  return "[grid-column:span_var(--sortable-span)/span_var(--sortable-span)]";
};

export const sortableListSpanStyle = (span: SortableListSpan | undefined): CSSProperties | undefined => {
  if (span === undefined || span === "full") return undefined;
  return { "--sortable-span": span };
};
